import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { reviewsService, LiveGoogleReview } from '../services/reviews';
import { useSettings } from '../hooks/useSettings';
import { GoogleReviewsWidget } from './GoogleReviewsWidget';
import { GoogleReviewsLiveWidget } from './GoogleReviewsLiveWidget';

function resolveGoogleReviewUrl(settings: ReturnType<typeof useSettings>['settings']) {
  const custom = settings?.social_google_review?.trim();
  if (custom) return custom;
  const placeId = settings?.google_place_id?.trim();
  if (placeId) {
    return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
  }
  return '';
}

export default function ReviewSection() {
    const { settings, loading: settingsLoading } = useSettings();
    const widgetEmbed = settings?.google_reviews_widget_embed?.trim();
    const placeId = settings?.google_place_id?.trim();
    const reviewUrl = resolveGoogleReviewUrl(settings);

    const [googleReviews, setGoogleReviews] = useState<LiveGoogleReview[]>([]);
    const [loading, setLoading] = useState(true);

    const useExternalWidget = Boolean(widgetEmbed);
    const useGoogleLive = !useExternalWidget && Boolean(placeId);

    useEffect(() => {
        if (settingsLoading) return;

        if (useExternalWidget) {
            setLoading(false);
            return;
        }

        if (!useGoogleLive) {
            setLoading(false);
            return;
        }

        reviewsService
            .getGoogleLive()
            .then(setGoogleReviews)
            .catch(() => setGoogleReviews([]))
            .finally(() => setLoading(false));
    }, [settingsLoading, useExternalWidget, useGoogleLive]);

    if (loading || settingsLoading) {
        return (
            <div className="py-24 flex flex-col items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary-500 animate-spin mb-4" />
                <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Chargement des avis Google...</p>
            </div>
        );
    }

    return (
        <section className="py-24 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-16">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-black text-gray-950">Avis Clients</h2>
                        <p className="text-gray-500 mt-2 font-medium">
                            Avis Google en direct — L Benna Production, Djerba.
                        </p>
                    </div>
                </div>

                {useExternalWidget ? (
                    <GoogleReviewsWidget embedCode={widgetEmbed!} />
                ) : useGoogleLive ? (
                    <GoogleReviewsLiveWidget reviews={googleReviews} reviewUrl={reviewUrl} />
                ) : (
                    <div className="py-20 text-center bg-gray-50 rounded-[3rem] border border-dashed border-gray-200">
                        <p className="text-gray-400 font-medium">Configurez le Google Place ID dans l&apos;admin.</p>
                    </div>
                )}
            </div>
        </section>
    );
}
